import { useMemo } from "react";

import { Invoice, Location } from "../../../redux/apis/types";
import { useSelector } from "react-redux";
import { State } from "../../../redux/root-reducer";

export const useFilterInvoice = (
  invoices: Invoice[],
  search: string[],
  locationId?: number,
  locationIds?: number[],
  branchIds?: number[]
) => {
  const { locations, branches } = useSelector((state: State) => state.Clients);

  let location: Location | undefined;
  if (locationId) {
    location = locations.find((item) => item.id === locationId);
  }

  // Subcontractor invoice work items only store the location/branch *name*
  // (no id), so map the selected ids to a set of names to match against
  // data.work[].planning.location.name / .branch.name. Keyed string avoids
  // re-runs on each new array reference from the parent.
  const locationKey = JSON.stringify(locationIds ?? []);
  const selectedLocationNames = useMemo(() => {
    if (!locationIds || locationIds.length === 0) return null;
    const names = locations
      .filter((item) => locationIds.includes(item.id))
      .map((item) => item.name?.toLowerCase())
      .filter(Boolean) as string[];
    return names.length ? new Set(names) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, locationKey]);

  const branchKey = JSON.stringify(branchIds ?? []);
  const selectedBranchNames = useMemo(() => {
    if (!branchIds || branchIds.length === 0) return null;
    const names = branches
      .filter((item) => branchIds.includes(item.id))
      .map((item) => item.name?.toLowerCase())
      .filter(Boolean) as string[];
    return names.length ? new Set(names) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, branchKey]);

  const filter = useMemo(() => {
    let copy = [...invoices];

    for (let i = 0; i < search.length; i++) {
      // console.log("searchhhhh : ", search);
      copy = copy.filter(
        (item) =>
          item.data?.description
            ?.toLowerCase()
            .includes(search[i]?.toLowerCase()) ||
          item.data?.totalIncl
            ?.toLowerCase()
            .includes(search[i]?.toLowerCase()) ||
          item.invoice_number.toString().includes(search[i]?.toLowerCase()) ||
          item.data?.description
            ?.toString()
            .includes(search[i]?.toLowerCase()) ||
          item?.branch?.name
            ?.toLowerCase()
            .includes(search[i]?.toLowerCase()) ||
          item?.subcontractor?.name
            ?.toLowerCase()
            .includes(search[i]?.toLowerCase()) ||
          item.data.work.some((workItem) =>
            (workItem as any)?.planning?.location?.name
              ?.toLowerCase()
              .includes(search[i]?.toLowerCase())
          ) ||
          item.data.work.some((workItem) =>
            (workItem as any)?.locationName
              ?.toLowerCase()
              .includes(search[i]?.toLowerCase())
          ) ||
          item.data.work.some((workItem) =>
            (workItem as any)?.planning?.branch?.name
              ?.toLowerCase()
              .includes(search[i]?.toLowerCase())
          )
      );
    }

    if (location) {
      copy = copy.filter((item) =>
        item.data.work.some(
          (workItem) =>
            (workItem as any)?.locationName
              .split(" / ")[0]
              ?.toLowerCase()
              ?.includes(location?.name?.toLowerCase()) ||
            (workItem as any)?.equipment?.service
              ?.toLowerCase()
              ?.includes(location?.name?.toLowerCase())
        )
      );
    }

    if (selectedLocationNames) {
      copy = copy.filter((item) =>
        item.data?.work?.some((workItem) => {
          const planningName = (workItem as any)?.planning?.location?.name?.toLowerCase();
          const prefixName = (workItem as any)?.locationName
            ?.split(" / ")[0]
            ?.toLowerCase();
          return (
            (!!planningName && selectedLocationNames.has(planningName)) ||
            (!!prefixName && selectedLocationNames.has(prefixName))
          );
        })
      );
    }

    if (selectedBranchNames) {
      copy = copy.filter((item) =>
        item.data?.work?.some((workItem) => {
          const branchName = (workItem as any)?.planning?.branch?.name?.toLowerCase();
          return !!branchName && selectedBranchNames.has(branchName);
        })
      );
    }

    return copy.map((item) => ({
      ...item,
      key: item.id,
    }));
  }, [invoices, search, selectedLocationNames, selectedBranchNames]);

  return filter;
};
